import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import i18n from "i18n-react";
import { modalForbidden } from "../components/DefaultAjax";
import SecureAjax from "../utils/SecureAjax";

export const ORDERS_ORG_MODULE_CODE = "VISITS";
export const ORDERS_DASHBOARD_MAX_RANGE_DAYS = 365;
export const ORDERS_DASHBOARD_RANKING_ENDPOINTS = {
    sellers: "orders/dashboardSellersFromAppWeb",
    products: "orders/dashboardProductsFromAppWeb",
    productsByRevenue: "orders/dashboardProductsByRevenueFromAppWeb",
    customers: "orders/dashboardCustomersFromAppWeb"
};

const parseResponseData = (data) => {
    if (typeof data === "string" && data !== "") {
        return JSON.parse(data);
    }
    return data;
};

const getPresetRange = (days) => {
    const endDate = moment().subtract(1, "day");
    const startDate = endDate.clone().subtract(days - 1, "days");

    return {
        dateStart: startDate.format("YYYY-MM-DD"),
        dateEnd: endDate.format("YYYY-MM-DD")
    };
};

const defaultRange = getPresetRange(15);
export const getTodayDate = () => moment().format("YYYY-MM-DD");

export const initialOrdersDashboardFilters = {
    ...defaultRange,
    preset: "15",
    type: "",
    ion: "",
    gon: "",
    user: "",
    status: "",
    customerReplicaId: "",
    officeReplicaId: ""
};

const normalizeOrgNodes = (result) => {
    const users = [{ value: "", label: "", username: "" }];
    const groups = [{ value: "", label: "" }];
    const profiles = [{ value: "", label: "" }];

    (result?.eonList || []).forEach((user) => {
        const username = user.username || "";
        const displayName = user.fullname || username;
        const usernameSuffix = username ? ` - ${username}` : "";
        users.push({
            value: username,
            label: `${displayName}${usernameSuffix}`,
            id: user.id,
            username
        });
    });

    (result?.gonList || []).forEach((group) => {
        groups.push({
            value: group.id || "",
            label: group.description || ""
        });
    });

    (result?.ionList || []).forEach((profile) => {
        profiles.push({
            value: profile.id || "",
            label: profile.description || ""
        });
    });

    return { users, groups, profiles };
};

export const buildOrdersDashboardRequest = (currentFilters) => ({
    dateStart: currentFilters.dateStart,
    dateEnd: currentFilters.dateEnd,
    type: currentFilters.type,
    ion: currentFilters.type === "1" ? currentFilters.ion : "",
    gon: currentFilters.type === "2" ? currentFilters.gon : "",
    user: currentFilters.type === "3" ? currentFilters.user : "",
    customerReplicaId: currentFilters.customerReplicaId,
    officeReplicaId: currentFilters.officeReplicaId,
    statuses: currentFilters.status ? [currentFilters.status] : []
});

export const sanitizeOrdersDashboardFilters = (currentFilters) => ({
    ...currentFilters,
    type: "",
    ion: "",
    gon: "",
    user: ""
});

const validateDateField = (errors, fieldName, fieldValue, dateValue, today) => {
    if (!fieldValue) {
        errors[fieldName] = i18n.translate("orders_dashboard_date_required");
        return;
    }

    if (!dateValue.isValid() || dateValue.isAfter(today, "day")) {
        errors[fieldName] = i18n.translate("orders_dashboard_date_cannot_be_future");
    }
};

const validateDateRange = (errors, dateStart, dateEnd) => {
    if (!dateStart.isValid() || !dateEnd.isValid()) {
        return;
    }

    if (dateStart.isAfter(dateEnd, "day")) {
        errors.dateStart = i18n.translate("orders_dashboard_start_after_end");
        return;
    }

    if (dateEnd.diff(dateStart, "days") + 1 > ORDERS_DASHBOARD_MAX_RANGE_DAYS) {
        errors.dateEnd = i18n.translate("orders_dashboard_max_range_exceeded");
    }
};

const validateVisibilityFilter = (errors, currentFilters) => {
    const visibilityFieldByType = {
        1: "ion",
        2: "gon",
        3: "user"
    };
    const visibilityField = visibilityFieldByType[currentFilters.type];

    if (visibilityField && !currentFilters[visibilityField]) {
        errors[visibilityField] = i18n.translate("orders_dashboard_visibility_value_required");
    }
};

export const validateOrdersDashboardFilters = (currentFilters) => {
    const errors = {};
    const today = moment(getTodayDate(), "YYYY-MM-DD", true);
    const dateStart = moment(currentFilters.dateStart, "YYYY-MM-DD", true);
    const dateEnd = moment(currentFilters.dateEnd, "YYYY-MM-DD", true);

    validateDateField(errors, "dateStart", currentFilters.dateStart, dateStart, today);
    validateDateField(errors, "dateEnd", currentFilters.dateEnd, dateEnd, today);
    validateDateRange(errors, dateStart, dateEnd);
    validateVisibilityFilter(errors, currentFilters);

    return errors;
};

export const buildOrdersDashboardRankingRequest = (currentFilters, pagination = null) => ({
    ...buildOrdersDashboardRequest(currentFilters),
    ...(pagination
        ? {
            page: pagination.page || 0,
            size: pagination.size || 25,
            search: pagination.search || ""
        }
        : {})
});

export const useOrdersDashboard = ({ setModalContent }) => {
    const [filters, setFilters] = useState(initialOrdersDashboardFilters);
    const [activeFilters, setActiveFilters] = useState(initialOrdersDashboardFilters);
    const [dashboard, setDashboard] = useState(null);
    const [orgNodes, setOrgNodes] = useState({ users: [], groups: [], profiles: [] });
    const [loading, setLoading] = useState(false);
    const [loadingFilters, setLoadingFilters] = useState(false);
    const [error, setError] = useState("");
    const [filterErrors, setFilterErrors] = useState({});
    const [lastLoadedAt, setLastLoadedAt] = useState(null);

    const handleError = useCallback((errorResponse) => {
        setLoading(false);
        setLoadingFilters(false);

        if (errorResponse.response && errorResponse.response.status === 403 && setModalContent) {
            setTimeout(() => {
                setModalContent(modalForbidden());
            }, 10);
            return;
        }

        if (errorResponse.response && errorResponse.response.status === 400) {
            setError(i18n.translate("orders_dashboard_invalid_filters"));
            return;
        }

        setError(errorResponse.message || i18n.translate("orders_dashboard_load_error"));
    }, [setModalContent]);

    const fetchDashboard = useCallback((currentFilters = initialOrdersDashboardFilters) => {
        const validationErrors = validateOrdersDashboardFilters(currentFilters);
        setFilterErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            setError("");
            return Promise.resolve(false);
        }

        setLoading(true);
        setError("");

        return SecureAjax.post("orders/dashboardKpisFromAppWeb", buildOrdersDashboardRequest(currentFilters))
            .then((res) => {
                if (res) {
                    setDashboard(parseResponseData(res.data));
                    setActiveFilters(currentFilters);
                    setLastLoadedAt(new Date());
                }
                setLoading(false);
            })
            .catch(handleError);
    }, [handleError]);

    const fetchDashboardRanking = useCallback((rankingType, currentFilters = initialOrdersDashboardFilters, pagination = null) => {
        const endpoint = ORDERS_DASHBOARD_RANKING_ENDPOINTS[rankingType];
        if (!endpoint) {
            return Promise.reject(new Error(`Unknown ranking type: ${rankingType}`));
        }

        return SecureAjax.post(endpoint, buildOrdersDashboardRankingRequest(currentFilters, pagination))
            .then((res) => parseResponseData(res?.data) || [])
            .catch((errorResponse) => {
                if (errorResponse.response && errorResponse.response.status === 403 && setModalContent) {
                    setTimeout(() => {
                        setModalContent(modalForbidden());
                    }, 10);
                }
                throw errorResponse;
            });
    }, [setModalContent]);

    const loadOrgNodes = useCallback(() => {
        setLoadingFilters(true);

        SecureAjax.get(`orgNode/allOrgNodeList?moduleCode=${ORDERS_ORG_MODULE_CODE}`)
            .then((res) => {
                if (res) {
                    setOrgNodes(normalizeOrgNodes(parseResponseData(res.data)));
                }
                setLoadingFilters(false);
            })
            .catch(handleError);
    }, [handleError]);

    useEffect(() => {
        loadOrgNodes();
        fetchDashboard(initialOrdersDashboardFilters);
    }, [fetchDashboard, loadOrgNodes]);

    const updateFilter = useCallback((name, value) => {
        setFilterErrors((prev) => ({
            ...prev,
            [name]: ""
        }));
        setFilters((prev) => ({
            ...prev,
            [name]: value
        }));
    }, []);

    const updateType = useCallback((value) => {
        setFilterErrors({});
        setFilters((prev) => ({
            ...prev,
            type: value,
            ion: "",
            gon: "",
            user: ""
        }));
    }, []);


    const applyPreset = useCallback((days) => {
        setFilterErrors({});
        setFilters((prev) => ({
            ...prev,
            ...getPresetRange(days),
            preset: String(days)
        }));
    }, []);

    const setCustomDate = useCallback((name, value) => {
        setFilterErrors((prev) => ({
            ...prev,     
            [name]: ""
        }));
        setFilters((prev) => ({
            ...prev,
            [name]: value,
            preset: "custom"
        }));
    }, []);

    return {
        dashboard,
        filters,
        activeFilters,
        orgNodes,
        loading,
        loadingFilters,
        error,
        filterErrors,
        lastLoadedAt,
        updateFilter,
        updateType,
        applyPreset,
        setCustomDate,
        fetchDashboard,
        fetchDashboardRanking
    };
};
